import { useEffect, useRef, useState } from "react"

export function useActiveScrollItem<T extends string>(
    featureRefs: React.MutableRefObject<Map<T, HTMLDivElement>>,
    features: { permaLink: T }[] | undefined,
) {
    const [activeItemId, setActiveItemId] = useState<T | null>(null)
    const activeItemIdRef = useRef(activeItemId)
    activeItemIdRef.current = activeItemId

    // Register scroll listener only when features change, not on every activeItemId change
    useEffect(() => {
        if (!features || features.length === 0) return

        const handleScroll = () => {
            let closestItem: { permaLink: T | ""; distance: number } = {
                permaLink: "" as T,
                distance: Infinity,
            }

            featureRefs.current.forEach((element, permaLink) => {
                if (!element) return

                const rect = element.getBoundingClientRect()

                // Skip if completely below viewport
                if (rect.bottom < 0) return

                const distance = Math.abs(rect.top - 20)
                if (distance < closestItem.distance) {
                    closestItem = { permaLink, distance }
                }
            })

            if (closestItem.permaLink && closestItem.permaLink !== activeItemIdRef.current) {
                setActiveItemId(closestItem.permaLink)
            }
        }

        let ticking = false
        const onScroll = () => {
            if (!ticking) {
                ticking = true
                requestAnimationFrame(() => {
                    handleScroll()
                    ticking = false
                })
            }
        }

        window.addEventListener("scroll", onScroll, { passive: true })
        handleScroll()

        return () => {
            window.removeEventListener("scroll", onScroll)
        }
    }, [features])

    // Set initial item when features load
    useEffect(() => {
        if (features && features.length > 0 && !activeItemId) {
            setActiveItemId(features[0].permaLink)
        }
    }, [features])

    return { activeItemId, setActiveItemId }
}
